import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Eye, Sparkles, Compass, HandHeart } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="container-up pt-16 md:pt-24 pb-20">
        <div className="max-w-3xl reveal">
          <span className="section-label">À propos</span>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            Bonjour, moi c&apos;est
            <em className="italic font-light"> Vinciane</em>.
          </h1>
          <p className="mt-8 text-up-muted text-lg leading-relaxed">
            J&apos;accompagne les entreprises locales à développer une
            présence en ligne moderne, élégante et efficace.
          </p>
          <p className="mt-5 text-up-muted leading-relaxed">
            J&apos;ai créé Breizh Boost Studio avec une conviction simple&nbsp;: les
            artisans, indépendants et petites structures bretonnes méritent
            des sites à la hauteur de leur savoir-faire, sans jargon, sans
            surenchère, et avec une vraie relation humaine.
          </p>
          <p className="mt-5 text-up-muted leading-relaxed">
            Mon approche repose sur l&apos;écoute, la simplicité et un
            attachement profond à la Bretagne. Un site, des photos, une
            visibilité&nbsp;: trois pièces d&apos;un même puzzle, un seul
            interlocuteur.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary group" data-testid="about-cta">
              Échangeons
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* VALEURS — Bento asymétrique */}
      <section
        className="bg-up-sand/50 border-y border-up-taupe/30"
        data-testid="about-values"
      >
        <div className="container-up py-24 md:py-32">
          <div className="max-w-3xl mb-12 md:mb-16 reveal">
            <span className="section-label">Ma vision</span>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Cinq <em className="italic font-light">mots</em>,
              <br />
              une manière de travailler.
            </h2>
          </div>

          {/* Bento grid 6 colonnes asymétrique */}
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-5">
            {/* Bloc 01 — Proximité (grand, fond brun) */}
            <article
              data-testid="value-0"
              className="reveal sm:col-span-4 sm:row-span-2 relative overflow-hidden rounded-3xl bg-up-brown text-up-offwhite p-8 md:p-10 lg:p-12 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(47,73,110,0.45)]"
            >
              <div className="absolute inset-0 grain opacity-50" aria-hidden />
              <div
                className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-up-sand/10 blur-3xl"
                aria-hidden
              />
              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-up-sand/20">
                    <HandHeart className="h-5 w-5 text-up-sand" strokeWidth={1.6} />
                  </span>
                  <span className="font-serif italic text-up-sand/70 text-lg tracking-tight">
                    01
                  </span>
                </div>
                <h3 className="mt-10 md:mt-14 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight !text-up-offwhite">
                  Proximité.
                </h3>
                <p className="mt-6 md:mt-8 text-up-sand text-base md:text-lg leading-[1.7] max-w-md">
                  Un accompagnement proche, humain, sans langage compliqué.
                </p>
                <div
                  aria-hidden
                  className="mt-auto pt-10 md:pt-14 flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-up-sand/70"
                >
                  <span className="h-px w-10 bg-up-sand/40" />
                  Cœur de l&apos;approche
                </div>
              </div>
            </article>

            {/* Bloc 02 — Écoute (carré clair) */}
            <article
              data-testid="value-1"
              className="reveal sm:col-span-2 relative overflow-hidden rounded-3xl bg-up-offwhite ring-1 ring-up-taupe/40 p-7 md:p-8 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(47,73,110,0.3)] flex flex-col"
              style={{ transitionDelay: "100ms" }}
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-up-sand-soft text-up-brown">
                  <Eye className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <span className="font-serif italic text-up-brown/40 text-base tracking-tight">
                  02
                </span>
              </div>
              <h3 className="mt-6 font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight">
                Écoute.
              </h3>
              <p className="mt-3 text-sm text-up-muted leading-relaxed">
                Comprendre votre activité avant de proposer quoi que ce soit.
              </p>
            </article>

            {/* Bloc 03 — Créativité (carte fond beige foncé Breizh Boost) */}
            <article
              data-testid="value-2"
              className="reveal sm:col-span-2 relative overflow-hidden rounded-3xl p-7 md:p-8 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(47,73,110,0.3)] flex flex-col"
              style={{ background: "#DCC6A3", border: "1px solid rgba(47,73,110,0.18)", transitionDelay: "180ms" }}
            >
              <div
                aria-hidden
                className="absolute top-0 right-0 h-32 w-32 rounded-full blur-2xl"
                style={{ background: "rgba(47,73,110,0.10)" }}
              />
              <div className="relative flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-up-brown text-up-offwhite">
                  <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <span className="font-serif italic text-base tracking-tight text-up-brown/50">
                  03
                </span>
              </div>
              <h3 className="relative mt-6 font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight text-up-brown">
                Créativité.
              </h3>
              <p className="relative mt-3 text-sm leading-relaxed text-up-brown/75">
                Des sites élégants qui vous ressemblent vraiment.
              </p>
            </article>

            {/* Bloc 04 — Modernité (horizontal large) */}
            <article
              data-testid="value-3"
              className="reveal sm:col-span-3 relative overflow-hidden rounded-3xl bg-up-offwhite ring-1 ring-up-taupe/40 p-7 md:p-8 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(47,73,110,0.3)]"
              style={{ transitionDelay: "240ms" }}
            >
              <div className="flex items-start gap-5">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-up-brown text-up-offwhite shrink-0">
                  <Compass className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight">
                      Modernité.
                    </h3>
                    <span className="font-serif italic text-up-brown/40 text-base tracking-tight">
                      04
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-up-muted leading-relaxed max-w-md">
                    Des outils actuels, simples à prendre en main.
                  </p>
                </div>
              </div>
            </article>

            {/* Bloc 05 — Sur-mesure (horizontal large avec accent) */}
            <article
              data-testid="value-4"
              className="reveal sm:col-span-3 relative overflow-hidden rounded-3xl bg-up-sand/60 ring-1 ring-up-taupe/40 p-7 md:p-8 transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(47,73,110,0.3)]"
              style={{ transitionDelay: "300ms" }}
            >
              <div className="flex items-start gap-5">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-up-brown text-up-offwhite shrink-0">
                  <Heart className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-2xl md:text-[1.75rem] leading-tight tracking-tight">
                      Sur-mesure.
                    </h3>
                    <span className="font-serif italic text-up-brown/40 text-base tracking-tight">
                      05
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-up-muted leading-relaxed max-w-md">
                    Chaque projet est unique, pensé pour votre réalité.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* BRETAGNE supprimée — section retirée à la demande */}
    </div>
  );
}
